import React from "react";
import { useNavigate } from "react-router-dom";
import { FaFireExtinguisher, FaRegLightbulb, FaTools, FaCheckCircle } from "react-icons/fa";
import { MdOutlineLocalFireDepartment } from "react-icons/md";
import { BsCloudFog2, BsLightningCharge } from "react-icons/bs";
import p1 from "../assets/FireTwo.jpg";
import p2 from "../assets/FireThree.jpg";
import p3 from "../assets/FireFour.jpg";
import p5 from "../assets/FireFive.jpg";
import smoke from "../assets/smoke.jpg";
import alarm from "../assets/firealarm.jpg";
import suppression from "../assets/suppression.jpg";
import FireOne from "../assets/FireOne.jpg";


const fireServices = [
  {
    title: "Fire Alarm",
    subtitle: "System Design & Installation",
    icon: <MdOutlineLocalFireDepartment size={28} className="text-white" />,
    image: alarm,
  },
  {
    title: "Smoke & Heat",
    subtitle: "Detection Systems",
    icon: <BsCloudFog2 size={28} className="text-white" />,
    image: smoke,
  },
  {
    title: "Suppression",
    subtitle: "Gas, Foam, Water Mist",
    icon: <BsLightningCharge size={28} className="text-white" />,
    image: suppression,
  },
  {
    title: "Extinguishers",
    subtitle: "Supply & Maintenance",
    icon: <FaFireExtinguisher size={28} className="text-white" />,
    image: p1,
  },
  {
    title: "Emergency Lighting",
    subtitle: "Lighting Systems",
    icon: <FaRegLightbulb size={28} className="text-white" />,
    image: p2,
  },
  {
    title: "Maintenance",
    subtitle: "Preventive & Corrective",
    icon: <FaTools size={28} className="text-white" />,
    image: p3,
  },
];

const reasons = [
  {
    title: "Certified Experts",
    subtitle: "Qualified fire safety professionals",
  },
  {
    title: "Quality Equipment",
    subtitle: "Approved & reliable fire systems",
  },
  {
    title: "Safety Compliance",
    subtitle: "Strict adherence to codes & standards",
  },
  {
    title: "Energy Efficient",
    subtitle: "Sustainable fire safety solutions",
  },
  {
    title: "24/7 Support",
    subtitle: "Emergency fire response anytime",
  },
  {
    title: "Proven Experience",
    subtitle: "Trusted across multiple industries",
  },
];

const projects = [
  { title: "Data Centers", desc: "Advanced fire detection for sensitive environments.", img: p1 },
  { title: "Factories", desc: "Industrial-grade suppression systems.", img: p2 },
  { title: "Commercial Buildings", desc: "Smart alarm and evacuation systems.", img: p3 },
];


const FireSystemService = () => {
  const navigate = useNavigate();

  return (
    <div className="w-full">
      {/* 1. Hero Section */}
      <section
        className="relative bg-black bg-cover bg-center bg-no-repeat h-[40vh] text-white py-20 px-4 text-center"
        style={{ backgroundImage: `url(${FireOne})` }}
      >
        <h1 className="text-[32px] font-bold mb-4">
          Complete Fire Protection Solutions
        </h1>
        <p className="text-lg max-w-2xl mx-auto mb-6">
          Designing, installing, and maintaining advanced fire detection and suppression
          systems.
        </p>
      </section>


      <section className="py-16 px-4 max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold mb-4">What We Do</h2>
          <p className="text-gray-600 max-w-3xl mx-auto">
            We provide end-to-end fire protection services, from system design
            and installation to testing, commissioning, and maintenance. Our
            solutions meet international safety standards, ensuring compliance
            and maximum protection for your property.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-16">
          {fireServices.map((service, idx) => (
            <div
              key={idx}
              className="relative h-64 group"
            >
              {/* Background Image */}
              <img
                src={service.image}
                alt={service.title}
                className="absolute inset-0 w-full h-full object-cover transform group-hover:scale-105 transition"
              />

              {/* Dark Overlay */}
              <div className="absolute inset-0 bg-black/50"></div>

              {/* Text Content */}
              <div className="relative flex flex-col items-center justify-end h-full pb-12 text-white text-center px-4">
                <h3 className="text-sm sm:text-lg md:text-xl lg:text-2xl font-semibold">
                  {service.title}
                </h3>
                <p className="text-sm sm:text-lg md:text-xl">
                  {service.subtitle}
                </p>
              </div>

              {/* Icon at Bottom */}
              <div className="absolute -bottom-9 left-1/2 transform -translate-x-1/2 bg-orange-500 py-5 px-8">
                {service.icon}
              </div>
            </div>
          ))}
        </div>

      </section>

      {/* 4. Why Choose Us */}
      <section className="py-12 px-4 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 items-center gap-10">

          {/* Left Content */}
          <div>
            <p className="text-gray-500 mb-2">What we offer</p>
            <h2 className="text-3xl font-bold mb-4">
              Why choose our <span className="text-orange-600">Fire Safety Solutions</span>?
            </h2>
            <p className="text-gray-600 mb-8">
              We provide end-to-end fire safety services designed to protect lives,
              property, and ensure compliance with industry standards.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 bg-white p-6 rounded-lg shadow">
              {reasons.map((reason, idx) => (
                <div key={idx} className="flex items-start gap-4">
                  <div className="bg-orange-500 text-white p-2 flex-shrink-0">
                    <FaCheckCircle size={20} />
                  </div>
                  <div>
                    <h4 className="font-semibold">{reason.title}</h4>
                    <p className="text-gray-500 text-sm">{reason.subtitle}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right Image */}
          <div className="relative">
            <img
              src={p5}
              alt="Fire Safety"
              className="rounded-lg shadow-lg w-full object-cover"
            />
          </div>
        </div>
      </section>

      {/* 5. Projects & Applications */}
      <section className="bg-gray-50 py-12 px-4">
        <div className="max-w-6xl mx-auto">
          {/* Header with vertical line */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center">
              <div className="w-1 h-10 bg-orange-600 mr-3"></div> {/* vertical line */}
              <h2 className="text-3xl font-bold text-gray-800">Projects & Applications</h2>
            </div>
            <button onClick={() => navigate("/projects")} className="px-4 py-2 bg-orange-600 text-white rounded hover:bg-orange-700 transition">
              View All
            </button>
          </div>

          {/* Project Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
            {projects.map((project, idx) => (
              <div key={idx} className="relative group overflow-hidden rounded-lg cursor-pointer">
                <img
                  src={project.img}
                  alt={project.title}
                  className="w-full h-64 object-cover transition-transform duration-300 group-hover:scale-110"
                />
                {/* Overlay with text */}
                <div className="absolute inset-0 bg-black bg-opacity-50 flex flex-col items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 text-center px-4">
                  <h3 className="text-white text-xl md:text-2xl lg:text-3xl font-semibold mb-2">{project.title}</h3>
                  <p className="text-white text-sm md:text-xl">{project.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>



      {/* 7. Call-to-Action Section */}
      <section
        className="relative  text-white py-16 px-4 text-center mb-36"
        style={{
          backgroundImage: `url(${p1})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        {/* Transparent overlay */}
        <div className="absolute inset-0 bg-black bg-opacity-50"></div>

        {/* Content */}
        <div className="relative z-10 max-w-3xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6">
            Let’s Protect Your Property — Safely and Efficiently
          </h2>
          <div className="space-x-4">
            <button onClick={() => navigate("/contact")} className="bg-white text-orange-600 font-semibold px-6 py-3 rounded shadow hover:bg-gray-100 transition">
              Contact Us
            </button>
            <button onClick={() => navigate("/quote")} className="bg-transparent border border-white font-semibold px-6 py-3 rounded hover:bg-white hover:text-orange-600 transition">
              Request a Quote
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FireSystemService;
